import h from 'hyperscript';
import last from 'lodash-es/last';

import type {
  BaseTableCellElement,
  BaseTableRowElement,
  HeaderCellContentsOptions
} from 'cadenza/ui/base-table/base-table';
import {
  createHeaderCellContents,
  getColumnIndexForBackend,
  isNonPrimitiveValueDto
} from 'cadenza/ui/base-table/base-table';
import type { ResultTableHierarchicalColumns } from 'cadenza/result-table/result-table';
import { getHeaderRowHasSubTitle } from 'cadenza/result-table/result-table-utils';
import type { RowMetaDataType } from 'cadenza/repository/model/row-meta-data-type';
import type { ColumnType } from 'cadenza/result-table/model/column-type';
import type { OlapAttributeDefinitionType } from 'cadenza/repository/model/olap-attribute-definition-type';
import type { TableColumnDto } from 'cadenza/ui/base-table/model/table-column-dto';
import type { PreparedResultTableColumn } from 'cadenza/result-table/prepared-result-table-column';
import type { OlapPivotTable } from 'cadenza/result-table/olap-pivot-table';

export const PIVOT_ROW_COL_CLASS = 'pivot-row-column';
export const LAST_PIVOT_ROW_COL_CLASS = 'last-pivot-row-column';
export const PIVOT_LAST_CELL_IN_GROUP_CLASS = 'last-cell-in-group';
export const COLUMN_TYPE_ROW_TOTAL_CLASS = getTdThColumnTypeClass('PIVOT_ROW_TOTAL');
export const COLUMN_WITH_HORIZONTAL_SORTING_BUTTON_IN_HEADER_CLASS = 'column--with-horizontal-sorting-button-in-header';

/**
 * Returns a marker class for denoting tr elements as rows of a given type.
 *
 * @param rowType - The type to return the class for.
 * @return The marker class for tr elements of the given type
 */
export function getTrRowTypeClass (rowType: RowMetaDataType) {
  return `row-type--${rowType}`;
}

/**
 * Returns a marker class for denoting td elements as part of columns of a given type.
 *
 * @param columnType - The type to return the class for
 * @return The marker class for td and th elements of the given type
 */
export function getTdThColumnTypeClass (columnType: ColumnType) {
  return `column-type--${columnType}`;
}

/**
 * Returns a marker class for denoting td elements as part of columns of a given type.
 *
 * @param olapAttributeType - The type to return the class for
 * @return The marker class for td and th elements of the given type
 */
export function getTdThOlapAttributeTypeClass (olapAttributeType: OlapAttributeDefinitionType) {
  return `olap-attribute-type--${olapAttributeType}`;
}

/**
 * Checks if the given table cell is a plain detail cell not part of a special type column or row
 * (subtotal, row total, top-n others or collapsed cell's subordinate dimension cell).
 *
 * @param tdElement - The cell element to check
 * @return True if tdElement is both, part of a DETAIL type column and row
 */
export function isDetailCell (tdElement: BaseTableCellElement) {
  const trElement = tdElement.parentElement;
  return isColumnOfType(tdElement, 'DETAIL')
    && (isRowOfType(trElement as HTMLTableRowElement, 'DETAIL')
      || isRowOfType(trElement as HTMLTableRowElement, 'COLLAPSED'))
    && !isSubordinateRowDimensionCellOfCollapsedRowCell(tdElement);
}

function isSubordinateRowDimensionCellOfCollapsedRowCell (tdElement: BaseTableCellElement) {
  const values = (tdElement.parentElement as BaseTableRowElement).dData.values;
  for (let i = 0; i <= tdElement.dDatatableColumnIndex; i++) {
    const value = values[i];
    if (isNonPrimitiveValueDto(value) && value.type === 'COLLAPSED') {
      return true;
    }
  }
  return false;
}

/**
 * Checks if the given table cell is part of a column with the given column type.
 *
 * @param tdElement - The cell element to check
 * @param columnType - The column type to check for
 * @return True if tdElement is part of a column with the given type
 */
function isColumnOfType (tdElement: HTMLTableCellElement, columnType: ColumnType) {
  return tdElement.classList.contains(getTdThColumnTypeClass(columnType));
}

/**
 * Checks if the given table row is part of a column with the given row type.
 *
 * @param trElement - The row element to check
 * @param rowType - The row type to check for
 * @return True if trElement is part of a row with the given type
 */
function isRowOfType (trElement: HTMLTableRowElement, rowType: RowMetaDataType) {
  return trElement.classList.contains(getTrRowTypeClass(rowType));
}

/*
 * Few things about pivot-table:
 * rowColumns have no children and appear in the leftmost part on the bottom row.
 * multiColumns have children which form a tree.
 * The bottom most level in the multiColumns has to be appended to the rowColumns to form the whole bottom header row.
 * The header rows above (if any) contain groupings of the lower levels.
 */

function createRowNumberColumnHeaderCell () {
  return h<HTMLTableCellElement>('th');
}

/**
 * Creates the html header structure. Pivot table can have multiple levels of headers (many tr elements in the thead)
 *
 * @param headerRowHasSubTitle - whether there is any columns with measureAggregation or Top N description
 * @param options - containing rowColumns and multiColumns
 * @param options.multiColumns - Columns with more that one level. (pivot)
 * @param options.rowColumns - Columns with one level.
 * @return the thead element as a string
 */
export function createHeaders (
  headerRowHasSubTitle: boolean,
  { multiColumns, rowColumns }: OlapPivotHierarchicalColumns) {
  const multiColumnHeaders: HTMLTableCellElement[][] = createMultiColumnHeaders(multiColumns, rowColumns.length);
  const rowColumnHeaders: HTMLTableCellElement[] = createTHs(rowColumns, {
    headerRowHasSubTitle,
    rowColumnLastGroupCell: [],
    isPivotRowColumn: true
  });
  return createHtmlTableHeader(rowColumnHeaders, multiColumnHeaders);
}

function createHtmlTableHeader (rowColumnHeaders: HTMLTableCellElement[], columnMultiHeaders: HTMLTableCellElement[][]) {
  const headers = [];
  for (let i = 0; i < columnMultiHeaders.length - 1; i++) {
    headers.push(h('tr.multiline-header-row', columnMultiHeaders[i]));
  }
  headers.push(h('tr', [
    createRowNumberColumnHeaderCell(),
    ...rowColumnHeaders,
    ...(last(columnMultiHeaders) ?? []) ]));

  return h<HTMLTableSectionElement>('thead', headers);
}

function createMultiColumnHeaders (
  multiColumns: PreparedResultTableColumn[][],
  rowColumnsLength: number) {
  const multiColumnHeaders: HTMLTableCellElement[][] = [];
  const lastRowColumnLastGroupCell = createLastRowColumnLastGroupCell(multiColumns);
  for (let i = 0; i < multiColumns.length; i++) {
    const rowColumnLastGroupCell = (i === multiColumns.length - 1) ? lastRowColumnLastGroupCell : [];
    multiColumnHeaders[i] = createTHs(multiColumns[i], {
      headerRowHasSubTitle: getHeaderRowHasSubTitle(multiColumns[i]),
      rowColumnLastGroupCell,
      isPivotRowColumn: false
    });
    if (rowColumnsLength && i < multiColumns.length - 1) {
      multiColumnHeaders[i].unshift(
        createRowNumberColumnHeaderCell(),
        h('th', { colSpan: rowColumnsLength }));
    }
  }
  return multiColumnHeaders;
}

function createLastRowColumnLastGroupCell (multiColumns: PreparedResultTableColumn[][]) {
  const previousRow = multiColumns[multiColumns.length - 2] ?? [];
  const lastRow: boolean[] = [];
  previousRow.forEach(column => {
    for (let i = colspan(column); i > 1; i--) {
      lastRow.push(false);
    }
    lastRow.push(true);
  });
  return lastRow;
}

function colspan (column: PreparedResultTableColumn) {
  return (column as PreparedOlapPivotTableColumn).colspan ?? 1;
}

export interface PivotHeaderColumnWithValue extends HTMLTableCellElement {
  dColumn: PreparedOlapPivotTableColumn;
}

/**
 * Creates the html header structure.
 */
function createTHs (
  /** Array of columns that have the hierarchy. */
  columns: PreparedOlapPivotTableColumn[],
  /** Contains optional parameters that are used to create the html header structure. */
  {
    /** Boolean which marks th as pivot row column element */
    isPivotRowColumn = false,
    /** Says if one of the columns is of type measure aggregation or has top n description */
    headerRowHasSubTitle = false,
    rowColumnLastGroupCell = [] as boolean[]
  } = {}): HTMLTableCellElement[] {
  const lastVisibleColumn = [ ...columns ].reverse().find(col => col.visible);
  return columns.map((column, index) => {
    const subTitle = column.headerSubTitle ?? column.topNInformation;
    const headerCellContentsOptions: HeaderCellContentsOptions = {
      subTitle,
      headerRowHasSubTitle
    };
    const th = h<PivotHeaderColumnWithValue>('th.ellipsis',
      createHeaderCellContents(column.title, headerCellContentsOptions));

    if (colspan(column) > 1) {
      th.colSpan = column.colspan!;
    }

    th.classList.add(getTdThColumnTypeClass(column.columnType));

    if (rowColumnLastGroupCell && rowColumnLastGroupCell[index]) {
      th.classList.add(PIVOT_LAST_CELL_IN_GROUP_CLASS);
    }

    /*
          Only columns that are not pivot row columns will have horizontal sorting buttons.
          Last column that is not of type ROW_TOTAL will have horizontal sorting buttons, that means
          that we need to check two cases:
          1. We're the penultimate column that is not of type ROW_TOTAL and the last column is of type
          ROW_TOTAL.
          2. We're the last column that is not of type ROW_TOTAL.
         */
    const isRowTotalHeader = column.columnType === 'PIVOT_ROW_TOTAL';
    if (!isRowTotalHeader && !isPivotRowColumn) {
      const nextColumn = columns[index + 1];
      if (!nextColumn || nextColumn?.columnType === 'PIVOT_ROW_TOTAL') {
        th.classList.add(COLUMN_WITH_HORIZONTAL_SORTING_BUTTON_IN_HEADER_CLASS);
      }
    }

    if (isPivotRowColumn) {
      th.classList.add(PIVOT_ROW_COL_CLASS);
      if (column === lastVisibleColumn) {
        th.classList.add(LAST_PIVOT_ROW_COL_CLASS);
      }
    }

    th.dColumn = column;

    return th;
  });
}

type PrepareFunction = (column: TableColumnDto) => PreparedOlapPivotTableColumn;

/**
 * Creates the (possibly hierarchical) structure of the columns in a pivot table: rowColumns and multiColumns.
 * Columns exceeding the columnCountLimit are omitted.
 *
 * @param columnsData - the array of column definitions
 * @param prepareFunction - function to apply on each columnData
 * @param columnCountLimit - Maximum number of columns to prepare. Resulting columns won't exceed this limit.
 * @return the column structure
 */
export function prepareHierarchicalColumns (
  columnsData: TableColumnDto[],
  prepareFunction: PrepareFunction,
  columnCountLimit: number): OlapPivotHierarchicalColumns {
  const multiColumns: PreparedOlapPivotTableColumn[][] = [];
  const rowColumns: PreparedOlapPivotTableColumn[] = [];
  const preparedColumns = { rowColumns, multiColumns };

  for (const columnData of columnsData) {
    if (columnData.children) {
      recursiveColumns(columnData, preparedColumns, 0, prepareFunction, columnCountLimit);
    } else {
      rowColumns.push(prepareFunction(columnData));
    }

    if (reachedColumnCountLimit(preparedColumns, columnCountLimit)) {
      break;
    }
  }
  return preparedColumns;
}

export function reachedColumnCountLimit ({ multiColumns, rowColumns }: ResultTableHierarchicalColumns, columnCountLimit: number) {
  // We ignore the row number column
  const totalNumberColumns = [ ...rowColumns, ...(multiColumns[multiColumns.length - 1] ?? []) ]
    .filter(c => c.visible).length;
  return totalNumberColumns >= columnCountLimit;
}

export interface PreparedOlapPivotTableColumn extends PreparedResultTableColumn {
  colspan?: number;
}

export interface OlapPivotHierarchicalColumns {
  rowColumns: PreparedOlapPivotTableColumn[];
  multiColumns: PreparedOlapPivotTableColumn[][];
}

function recursiveColumns (
  columnData: TableColumnDto,
  preparedColumns: OlapPivotHierarchicalColumns,
  level: number,
  prepareFunction: PrepareFunction,
  columnCountLimit: number) {
  // Add level array entry if not present
  if (!preparedColumns.multiColumns[level]) {
    preparedColumns.multiColumns[level] = [];
  }

  if (!columnData.children) {
    preparedColumns.multiColumns[level].push(prepareFunction(columnData));
    return 1;
  } else {
    let totalColSpan = 0;
    for (const child of columnData.children) {
      totalColSpan += recursiveColumns(child, preparedColumns, level + 1, prepareFunction, columnCountLimit);

      if (reachedColumnCountLimit(preparedColumns, columnCountLimit)) {
        break;
      }
    }
    preparedColumns.multiColumns[level].push(Object.assign({ colspan: totalColSpan }, prepareFunction(columnData)));

    return totalColSpan;
  }
}

export function isRowColumn (olapPivotTable: OlapPivotTable, columnIndex: number) {
  return olapPivotTable.getColumns()[getColumnIndexForBackend(columnIndex)]
    .olapAttributeDefinitionType === 'DIMENSION';
}
